import { addDays, format, subDays } from 'date-fns'

export interface Checkpoint {
  id: string
  name: string
  status: 'passed' | 'failed' | 'pending'
  timestamp: string
  details: string
  type: 'erp' | 'explicit' | 'hash' | 'api' | 'graph' | 'ai' | 'learn'
}

export interface Invoice {
  id: string
  amount: number
  obligor: string
  creditRating: 'A' | 'A-' | 'BBB+' | 'BBB'
  supplier: string // Anonymized
  issueDate: string
  dueDate: string
  tenorDays: number
  status: 'verified' | 'pending' | 'failed'
  checkpoints: Checkpoint[]
  poNumber: string
  grNumber: string
}

export interface Batch {
  id: string
  closeDate: string
  invoiceCount: number
  totalAmount: number
  avgTenor: number
  verificationRate: number
  invoices: Invoice[]
  obligorMix: Record<string, number> // Retailer -> Count
}

const RETAILERS = [
  { name: 'Target Corp', rating: 'A' },
  { name: 'Walmart Inc', rating: 'AA' },
  { name: 'Kroger Co', rating: 'BBB' },
  { name: 'Best Buy Co', rating: 'BBB+' },
  { name: 'Home Depot', rating: 'A' },
]

const TERMS = [30, 45, 60]

function generateCheckpoints(baseDate: Date, invoiceId: string): Checkpoint[] {
  const dateStr = (offset: number) =>
    format(subDays(baseDate, offset), "MM/dd HH:mm 'ET'")

  return [
    // Retailer Verification
    {
      id: '01',
      name: 'Invoice Exists',
      status: 'passed',
      timestamp: dateStr(0),
      details: 'Confirmed in SAP',
      type: 'erp',
    },
    {
      id: '02',
      name: 'PO Match',
      status: 'passed',
      timestamp: dateStr(0),
      details: `PO-${Math.floor(Math.random() * 100000)} matched`,
      type: 'erp',
    },
    {
      id: '03',
      name: 'Goods Receipt',
      status: 'passed',
      timestamp: dateStr(1),
      details: `GR-${Math.floor(Math.random() * 100000)} confirmed`,
      type: 'erp',
    },
    {
      id: '04',
      name: 'Amount Valid',
      status: 'passed',
      timestamp: dateStr(0),
      details: 'Within tolerance',
      type: 'erp',
    },
    {
      id: '05',
      name: 'Retailer Selects',
      status: 'passed',
      timestamp: dateStr(0),
      details: `Selected at ${dateStr(0)}`,
      type: 'explicit',
    },
    {
      id: '06',
      name: 'Duplicate Check',
      status: 'passed',
      timestamp: dateStr(0),
      details: 'Unique (SHA-256)',
      type: 'hash',
    },

    // Supplier Verification
    {
      id: '07',
      name: 'Historical Analysis',
      status: 'passed',
      timestamp: dateStr(2),
      details: '5yr relationship',
      type: 'graph',
    },
    {
      id: '08',
      name: 'Third-Party Data',
      status: 'passed',
      timestamp: dateStr(2),
      details: 'D&B verified',
      type: 'api',
    },
    {
      id: '09',
      name: 'KYC/AML',
      status: 'passed',
      timestamp: dateStr(5),
      details: 'Sanctions clear',
      type: 'api',
    },
    {
      id: '10',
      name: 'Bank Validation',
      status: 'passed',
      timestamp: dateStr(5),
      details: 'Account verified',
      type: 'api',
    },
    {
      id: '11',
      name: 'Entity Verification',
      status: 'passed',
      timestamp: dateStr(5),
      details: 'Active, good standing',
      type: 'api',
    },

    // Outcome
    {
      id: '12',
      name: 'Underwriting',
      status: 'passed',
      timestamp: dateStr(0),
      details: 'Approved 85%',
      type: 'ai',
    },
    {
      id: '13',
      name: 'Outcome Tracking',
      status: 'passed',
      timestamp: dateStr(0),
      details: 'Feeds model',
      type: 'learn',
    },
  ]
}

function generateInvoices(count: number, batchDate: Date): Invoice[] {
  return Array.from({ length: count }).map((_, i) => {
    const retailer = RETAILERS[Math.floor(Math.random() * RETAILERS.length)]
    const amount = Math.floor(Math.random() * (500000 - 15000) + 15000)
    const tenor = TERMS[Math.floor(Math.random() * TERMS.length)]
    const issueDate = subDays(batchDate, Math.floor(Math.random() * 5))

    return {
      id: `INV-${2024}-${Math.floor(Math.random() * 10000) + 1000}`,
      amount,
      obligor: retailer.name,
      creditRating: retailer.rating as any,
      supplier: `Supplier #${Math.floor(Math.random() * 9000) + 1000}`,
      issueDate: format(issueDate, 'MMM dd, yyyy'),
      dueDate: format(addDays(issueDate, tenor), 'MMM dd, yyyy'),
      tenorDays: tenor,
      status: 'verified',
      checkpoints: generateCheckpoints(batchDate, ''),
      poNumber: `PO-${Math.floor(Math.random() * 999999)}`,
      grNumber: `GR-${Math.floor(Math.random() * 999999)}`,
    }
  })
}

export function generateBatches(): Batch[] {
  const batches: Batch[] = []
  const baseDate = new Date() // Current date

  // Generate 5 batches
  for (let i = 0; i < 5; i++) {
    const closeDate = subDays(baseDate, i * 3) // Every 3 days roughly
    const invoiceCount = Math.floor(Math.random() * 30) + 20 // 20-50 invoices
    const invoices = generateInvoices(invoiceCount, closeDate)
    const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0)
    const avgTenor = Math.round(
      invoices.reduce((sum, inv) => sum + inv.tenorDays, 0) / invoiceCount,
    )

    // Calculate obligor mix
    const obligorMix: Record<string, number> = {}
    invoices.forEach((inv) => {
      obligorMix[inv.obligor] = (obligorMix[inv.obligor] || 0) + 1
    })

    batches.push({
      id: `BATCH-${format(closeDate, 'yyyyMMdd')}-${i + 1}`,
      closeDate: format(closeDate, 'MMM dd, yyyy HH:mm'),
      invoiceCount,
      totalAmount,
      avgTenor,
      verificationRate: 100,
      invoices,
      obligorMix,
    })
  }

  return batches
}

export const MOCK_BATCHES = generateBatches()
